import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Settings,
  Mail,
  Shield,
  Database,
  Palette,
  Bell,
  Save,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Upload,
  Download,
  Smartphone
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { apiService as api } from '../services/api';

const Container = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
`;

const Header = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1rem;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  height: fit-content;
`;

const SidebarMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MenuItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  text-align: left;
  transition: all 0.2s ease;

  ${props => props.$active ? `
    background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
    color: white;
  ` : `
    color: #666;
    &:hover {
      background: #f8f9fa;
      color: #333;
    }
  `}
`;

const Content = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ContentHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #f8f9fa;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const ContentTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ContentDescription = styled.p`
  color: #666;
  font-size: 0.875rem;
`;

const ContentBody = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Checkbox = styled.input`
  margin-right: 0.75rem;
  transform: scale(1.2);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: #333;
  font-size: 0.875rem;
  cursor: pointer;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      default:
        return `
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          &:hover { background: #f8f9fa; }
        `;
    }
  }}
`;

const Alert = styled.div<{ $variant: 'success' | 'warning' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'warning':
        return `
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
      case 'danger':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
    }
  }}
`;

const FileUpload = styled.div`
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #00d4aa;
    background: #f8f9ff;
  }
`;

export const AdminSettingsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [showPassword, setShowPassword] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [settingsStatus, setSettingsStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Email settings state
  const [emailSettings, setEmailSettings] = useState<any[]>([]);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [emailTestStatus, setEmailTestStatus] = useState<'success' | 'error' | null>(null);

  const [systemSettings, setSystemSettings] = useState<Record<string, string>>({});
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);

  const [backupFiles, setBackupFiles] = useState<any[]>([]);
  const [isLoadingBackup, setIsLoadingBackup] = useState(false);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [backupStatus, setBackupStatus] = useState<'success' | 'error' | null>(null);
  const backupInputRef = useRef<HTMLInputElement | null>(null);

  const menuItems = [
    { key: 'general', label: 'Geral', icon: <Settings size={16} /> },
    { key: 'email', label: 'Email', icon: <Mail size={16} /> },
    { key: 'appearance', label: 'Aparência', icon: <Palette size={16} /> },
    { key: 'mobile-app', label: 'Aplicativo Mobile', icon: <Smartphone size={16} /> },
    { key: 'notifications', label: 'Notificações', icon: <Bell size={16} /> },
    { key: 'security', label: 'Segurança', icon: <Shield size={16} /> },
    { key: 'backup', label: 'Backup', icon: <Database size={16} /> }
  ];

  // Load email settings when email section is active
  useEffect(() => {
    if (activeSection === 'email') {
      loadEmailSettings();
    }
  }, [activeSection]);

  useEffect(() => {
    loadSystemSettings();
  }, []);

  useEffect(() => {
    if (activeSection === 'backup') {
      loadBackupFiles();
    }
  }, [activeSection]);

  const loadEmailSettings = async () => {
    try {
      setIsLoadingEmail(true);
      const response = await api.getEmailSettings();

      if (response.success) {
        setEmailSettings(response.data);
      } else {
        console.error('Failed to load email settings');
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const loadSystemSettings = async () => {
    try {
      setIsLoadingSettings(true);
      const response = await api.getSystemSettings();
      if (response.success && Array.isArray(response.data)) {
        const mapped = response.data.reduce((acc: Record<string, string>, setting: any) => {
          acc[setting.key] = setting.value ?? '';
          return acc;
        }, {});
        setSystemSettings(mapped);
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const updateSystemSetting = (key: string, value: string) => {
    setSystemSettings(prev => ({ ...prev, [key]: value }));
  };

  const uploadAppearanceAsset = async (key: string, file?: File | null) => {
    if (!file) return;
    try {
      setIsUploadingAsset(true);
      const formData = new FormData();
      formData.append('key', key);
      formData.append('file', file);
      const response = await api.axiosInstance.post('/admin/system-settings/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.success && response.data.data?.url) {
        updateSystemSetting(key, response.data.data.url);
      }
    } catch (error) {
      console.error('Error uploading asset:', error);
      setSettingsStatus('error');
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const saveSystemSettings = async () => {
    try {
      setIsSavingSettings(true);
      setSettingsStatus('saving');
      const payload = Object.entries(systemSettings).map(([key, value]) => ({
        key,
        value,
      }));
      const response = await api.updateSystemSettingsBatch({ settings: payload });
      if (response.success) {
        setSettingsStatus('saved');
        setTimeout(() => setSettingsStatus('idle'), 3000);
      } else {
        setSettingsStatus('error');
      }
    } catch (error) {
      console.error('Error saving system settings:', error);
      setSettingsStatus('error');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const loadBackupFiles = async () => {
    try {
      setIsLoadingBackup(true);
      const response = await api.listBackups();
      if (response.success && Array.isArray(response.data)) {
        setBackupFiles(response.data);
      }
    } catch (error) {
      console.error('Error loading backups:', error);
    } finally {
      setIsLoadingBackup(false);
    }
  };

  const createBackup = async () => {
    try {
      setIsCreatingBackup(true);
      setBackupStatus(null);
      const response = await api.createBackup();
      if (response.success) {
        setBackupStatus('success');
        await loadBackupFiles();
        if (response.data?.filename) {
          try {
            const downloadResponse = await api.downloadBackup(response.data.filename);
            const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = response.data.filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (downloadError) {
            console.error('Error downloading backup:', downloadError);
          }
        }
      } else {
        setBackupStatus('error');
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      setBackupStatus('error');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const restoreBackup = async (payload: FormData) => {
    try {
      setIsRestoringBackup(true);
      setBackupStatus(null);
      const response = await api.restoreBackup(payload);
      if (response.success) {
        setBackupStatus('success');
        await loadBackupFiles();
      } else {
        setBackupStatus('error');
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      setBackupStatus('error');
    } finally {
      setIsRestoringBackup(false);
    }
  };

  const handleDownloadBackup = async (filename: string) => {
    try {
      const response = await api.downloadBackup(filename);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading backup:', error);
      setBackupStatus('error');
    }
  };

  const updateEmailSetting = (id: number, value: string) => {
    setEmailSettings(prev => prev.map(setting =>
      setting.id === id ? { ...setting, value } : setting
    ));
  };

  const saveEmailSettings = async () => {
    try {
      setIsSavingEmail(true);

      const settingsToUpdate = emailSettings.map(setting => ({
        id: setting.id,
        value: setting.value
      }));

      const response = await api.updateEmailSettingsBatch({
        settings: settingsToUpdate
      });

      if (response.success) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
        await loadEmailSettings(); // Reload to get fresh data
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setSaveStatus('error');
    } finally {
      setIsSavingEmail(false);
    }
  };

  const getSetting = (key: string, fallback = '') => systemSettings[key] ?? fallback;

  const testEmailConnection = async () => {
    try {
      setIsTestingEmail(true);
      setEmailTestStatus(null);
      const response = await api.testEmailConnection();

      if (response.success) {
        setEmailTestStatus('success');
        setTimeout(() => setEmailTestStatus(null), 5000);
      } else {
        setEmailTestStatus('error');
        setTimeout(() => setEmailTestStatus(null), 5000);
      }
    } catch (error: any) {
      setEmailTestStatus('error');
      setTimeout(() => setEmailTestStatus(null), 5000);
      console.error('Email test failed:', error);
    } finally {
      setIsTestingEmail(false);
    }
  };

  const openExternalLink = (url: string) => {
    const normalized = (url || '').trim();
    if (!normalized) {
      window.alert('Configure o link primeiro e salve as configurações.');
      return;
    }

    window.open(normalized, '_blank', 'noopener,noreferrer');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveStatus('saving');

    // Simular salvamento
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }, 2000);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <>
            <ContentHeader>
              <ContentTitle>
                <Settings size={20} />
                Configurações Gerais
              </ContentTitle>
              <ContentDescription>
                Configure as informações básicas do sistema
              </ContentDescription>
            </ContentHeader>
            <ContentBody>
              <Form onSubmit={handleSave}>
                <FormSection>
                  <SectionTitle>Informações do Sistema</SectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label>Nome do Sistema</Label>
                      <Input type="text" defaultValue="PontoFiel" />
                    </FormGroup>
                    <FormGroup>
                      <Label>Versão</Label>
                      <Input type="text" defaultValue="1.0.0" disabled />
                    </FormGroup>
                  </FormRow>
                  <FormGroup>
                    <Label>Descrição</Label>
                    <Textarea defaultValue="Sistema completo de fidelidade para empresas e clientes" />
                  </FormGroup>
                </FormSection>

                <FormSection>
                  <SectionTitle>Configurações de Contato</SectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label>Email de Suporte</Label>
                      <Input type="email" defaultValue="suporte@pontofiel.com" />
                    </FormGroup>
                    <FormGroup>
                      <Label>Telefone</Label>
                      <Input type="tel" defaultValue="(11) 9999-9999" />
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <Button type="submit" $variant="primary" disabled={saveStatus === 'saving'}>
                  <Save size={16} />
                  {saveStatus === 'saving' ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
              </Form>
            </ContentBody>
          </>
        );

      case 'email':
        return (
          <>
            <ContentHeader>
              <ContentTitle>
                <Mail size={20} />
                Configurações de Email
              </ContentTitle>
              <ContentDescription>
                Configure o servidor SMTP para envio de emails (aplicação automática)
              </ContentDescription>
            </ContentHeader>
            <ContentBody>
              {emailTestStatus && (
                <Alert $variant={emailTestStatus === 'error' ? 'danger' : emailTestStatus}>
                  {emailTestStatus === 'success' ? (
                    <>
                      <CheckCircle size={16} />
                      Teste de email enviado com sucesso!
                    </>
                  ) : (
                    <>
                      <AlertCircle size={16} />
                      Falha no teste de email. Verifique as configurações.
                    </>
                  )}
                </Alert>
              )}

              {isLoadingEmail ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Carregando...</div>
              ) : (
                <Form onSubmit={(e) => { e.preventDefault(); saveEmailSettings(); }}>
                  <FormSection>
                    <SectionTitle>Status</SectionTitle>
                    <FormGroup>
                      <Label>Email Habilitado</Label>
                      <Select
                        value={emailSettings.find(s => s.key === 'email_enabled')?.value || 'false'}
                        onChange={(e) => {
                          const setting = emailSettings.find(s => s.key === 'email_enabled');
                          if (setting) updateEmailSetting(setting.id, e.target.value);
                        }}
                      >
                        <option value="true">Ativo</option>
                        <option value="false">Inativo</option>
                      </Select>
                    </FormGroup>
                  </FormSection>

                  <FormSection>
                    <SectionTitle>Servidor SMTP</SectionTitle>
                    <FormRow>
                      <FormGroup>
                        <Label>Servidor</Label>
                        <Input
                          type="text"
                          placeholder="smtp.gmail.com"
                          value={emailSettings.find(s => s.key === 'email_smtp_host')?.value || ''}
                          onChange={(e) => {
                            const setting = emailSettings.find(s => s.key === 'email_smtp_host');
                            if (setting) updateEmailSetting(setting.id, e.target.value);
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Porta</Label>
                        <Input
                          type="number"
                          value={emailSettings.find(s => s.key === 'email_smtp_port')?.value || '587'}
                          onChange={(e) => {
                            const setting = emailSettings.find(s => s.key === 'email_smtp_port');
                            if (setting) updateEmailSetting(setting.id, e.target.value);
                          }}
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <Label>Usuário</Label>
                        <Input
                          type="email"
                          placeholder="noreply@pontofiel.com"
                          value={emailSettings.find(s => s.key === 'email_smtp_username')?.value || ''}
                          onChange={(e) => {
                            const setting = emailSettings.find(s => s.key === 'email_smtp_username');
                            if (setting) updateEmailSetting(setting.id, e.target.value);
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Senha</Label>
                        <div style={{ position: 'relative' }}>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={emailSettings.find(s => s.key === 'email_smtp_password')?.value || ''}
                            onChange={(e) => {
                              const setting = emailSettings.find(s => s.key === 'email_smtp_password');
                              if (setting) updateEmailSetting(setting.id, e.target.value);
                            }}
                          />
                          <Button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              padding: '0.5rem',
                              background: 'transparent',
                              border: 'none'
                            }}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </Button>
                        </div>
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <Label>Criptografia</Label>
                        <Select
                          value={emailSettings.find(s => s.key === 'email_encryption')?.value || 'tls'}
                          onChange={(e) => {
                            const setting = emailSettings.find(s => s.key === 'email_encryption');
                            if (setting) updateEmailSetting(setting.id, e.target.value);
                          }}
                        >
                          <option value="tls">TLS</option>
                          <option value="ssl">SSL</option>
                        </Select>
                      </FormGroup>
                    </FormRow>
                  </FormSection>

                  <FormSection>
                    <SectionTitle>Informações do Remetente</SectionTitle>
                    <FormRow>
                      <FormGroup>
                        <Label>Email do Remetente</Label>
                        <Input
                          type="email"
                          placeholder="noreply@pontofiel.com"
                          value={emailSettings.find(s => s.key === 'email_from_address')?.value || ''}
                          onChange={(e) => {
                            const setting = emailSettings.find(s => s.key === 'email_from_address');
                            if (setting) updateEmailSetting(setting.id, e.target.value);
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Nome do Remetente</Label>
                        <Input
                          type="text"
                          placeholder="PontoFiel"
                          value={emailSettings.find(s => s.key === 'email_from_name')?.value || ''}
                          onChange={(e) => {
                            const setting = emailSettings.find(s => s.key === 'email_from_name');
                            if (setting) updateEmailSetting(setting.id, e.target.value);
                          }}
                        />
                      </FormGroup>
                    </FormRow>
                  </FormSection>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <Button type="submit" $variant="primary" disabled={isSavingEmail}>
                      <Save size={16} />
                      {isSavingEmail ? 'Salvando...' : 'Salvar Configurações'}
                    </Button>

                    <Button
                      type="button"
                      $variant="secondary"
                      onClick={testEmailConnection}
                      disabled={isTestingEmail}
                    >
                      <Mail size={16} />
                      {isTestingEmail ? 'Testando...' : 'Testar Conexão'}
                    </Button>
                  </div>
                </Form>
              )}
            </ContentBody>
          </>
        );

      case 'appearance':
        return (
          <>
            <ContentHeader>
              <ContentTitle>
                <Palette size={20} />
                Aparência
              </ContentTitle>
              <ContentDescription>
                Personalize cores e imagens do sistema
              </ContentDescription>
            </ContentHeader>
            <ContentBody>
              {isLoadingSettings ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Carregando...</div>
              ) : (
                <Form onSubmit={(e) => { e.preventDefault(); saveSystemSettings(); }}>
                  <FormSection>
                    <SectionTitle>Identidade Visual</SectionTitle>
                    <FormRow>
                      <FormGroup>
                        <Label>Cor Primaria</Label>
                        <Input
                          type="color"
                          value={getSetting('appearance_primary_color', '#00d4aa')}
                          onChange={(e) => updateSystemSetting('appearance_primary_color', e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Cor Secundaria</Label>
                        <Input
                          type="color"
                          value={getSetting('appearance_secondary_color', '#0a1220')}
                          onChange={(e) => updateSystemSetting('appearance_secondary_color', e.target.value)}
                        />
                      </FormGroup>
                    </FormRow>
                    <FormGroup>
                      <Label>Logo</Label>
                      {getSetting('appearance_logo_url', '') && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <img
                            src={getSetting('appearance_logo_url', '')}
                            alt="Logo atual"
                            style={{ height: '48px', width: 'auto', borderRadius: '6px', border: '1px solid #e9ecef' }}
                          />
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => uploadAppearanceAsset('appearance_logo_url', e.target.files?.[0] || null)}
                        disabled={isUploadingAsset}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Favicon</Label>
                      {getSetting('appearance_favicon_url', '') && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <img
                            src={getSetting('appearance_favicon_url', '')}
                            alt="Favicon atual"
                            style={{ height: '32px', width: '32px', borderRadius: '6px', border: '1px solid #e9ecef' }}
                          />
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*,.ico"
                        onChange={(e) => uploadAppearanceAsset('appearance_favicon_url', e.target.files?.[0] || null)}
                        disabled={isUploadingAsset}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Background do Login</Label>
                      {getSetting('appearance_login_background_url', '') && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <img
                            src={getSetting('appearance_login_background_url', '')}
                            alt="Background do login"
                            style={{ height: '120px', width: '100%', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e9ecef' }}
                          />
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => uploadAppearanceAsset('appearance_login_background_url', e.target.files?.[0] || null)}
                        disabled={isUploadingAsset}
                      />
                    </FormGroup>
                  </FormSection>

                  <Button type="submit" $variant="primary" disabled={isSavingSettings}>
                    <Save size={16} />
                    {isSavingSettings ? 'Salvando...' : 'Salvar Aparencia'}
                  </Button>
                </Form>
              )}
            </ContentBody>
          </>
        );

      case 'notifications':
        return (
          <>
            <ContentHeader>
              <ContentTitle>
                <Bell size={20} />
                Notificacoes
              </ContentTitle>
              <ContentDescription>
                Configure alertas e comunicacoes administrativas
              </ContentDescription>
            </ContentHeader>
            <ContentBody>
              {isLoadingSettings ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Carregando...</div>
              ) : (
                <Form onSubmit={(e) => { e.preventDefault(); saveSystemSettings(); }}>
                  <FormSection>
                    <SectionTitle>Eventos do Sistema</SectionTitle>
                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        checked={getSetting('notifications_new_signup', 'true') === 'true'}
                        onChange={(e) => updateSystemSetting('notifications_new_signup', e.target.checked ? 'true' : 'false')}
                      />
                      Novo cadastro de empresa
                    </CheckboxLabel>
                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        checked={getSetting('notifications_payment_failed', 'true') === 'true'}
                        onChange={(e) => updateSystemSetting('notifications_payment_failed', e.target.checked ? 'true' : 'false')}
                      />
                      Pagamento com falha
                    </CheckboxLabel>
                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        checked={getSetting('notifications_subscription_expiring', 'true') === 'true'}
                        onChange={(e) => updateSystemSetting('notifications_subscription_expiring', e.target.checked ? 'true' : 'false')}
                      />
                      Assinatura expirando
                    </CheckboxLabel>
                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        checked={getSetting('notifications_weekly_report', 'false') === 'true'}
                        onChange={(e) => updateSystemSetting('notifications_weekly_report', e.target.checked ? 'true' : 'false')}
                      />
                      Relatorio semanal
                    </CheckboxLabel>
                  </FormSection>

                  <Button type="submit" $variant="primary" disabled={isSavingSettings}>
                    <Save size={16} />
                    {isSavingSettings ? 'Salvando...' : 'Salvar Notificacoes'}
                  </Button>
                </Form>
              )}
            </ContentBody>
          </>
        );

      case 'mobile-app':
        return (
          <>
            <ContentHeader>
              <ContentTitle>
                <Smartphone size={20} />
                Aplicativo Mobile
              </ContentTitle>
              <ContentDescription>
                Configure os links de download do app para Android e iOS
              </ContentDescription>
            </ContentHeader>
            <ContentBody>
              {isLoadingSettings ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Carregando...</div>
              ) : (
                <Form onSubmit={(e) => { e.preventDefault(); saveSystemSettings(); }}>
                  <FormSection>
                    <SectionTitle>Links de Download</SectionTitle>
                    <FormRow>
                      <FormGroup>
                        <Label>Android (APK/Play Store)</Label>
                        <Input
                          type="url"
                          placeholder="https://play.google.com/store/apps/details?id=..."
                          value={getSetting('mobile_android_download_url', '')}
                          onChange={(e) => updateSystemSetting('mobile_android_download_url', e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>iOS (App Store/TestFlight)</Label>
                        <Input
                          type="url"
                          placeholder="https://apps.apple.com/... ou https://testflight.apple.com/join/..."
                          value={getSetting('mobile_ios_download_url', '')}
                          onChange={(e) => updateSystemSetting('mobile_ios_download_url', e.target.value)}
                        />
                      </FormGroup>
                    </FormRow>
                  </FormSection>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <Button type="submit" $variant="primary" disabled={isSavingSettings}>
                      <Save size={16} />
                      {isSavingSettings ? 'Salvando...' : 'Salvar Links'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => openExternalLink(getSetting('mobile_android_download_url', ''))}
                    >
                      <Download size={16} />
                      Baixar Android
                    </Button>
                    <Button
                      type="button"
                      onClick={() => openExternalLink(getSetting('mobile_ios_download_url', ''))}
                    >
                      <Download size={16} />
                      Baixar iOS
                    </Button>
                  </div>
                </Form>
              )}
            </ContentBody>
          </>
        );

      case 'security':
        return (
          <>
            <ContentHeader>
              <ContentTitle>
                <Shield size={20} />
                Configurações de Segurança
              </ContentTitle>
              <ContentDescription>
                Gerencie as configurações de segurança do sistema
              </ContentDescription>
            </ContentHeader>
            <ContentBody>
              <Alert $variant="warning">
                <AlertCircle size={16} />
                Alterações nas configurações de segurança podem afetar o acesso ao sistema
              </Alert>

              <Form onSubmit={handleSave}>
                <FormSection>
                  <SectionTitle>Autenticação</SectionTitle>
                  <FormGroup>
                    <Label>Tempo de Sessão (minutos)</Label>
                    <Input type="number" defaultValue="480" min="30" max="1440" />
                  </FormGroup>
                  <CheckboxLabel>
                    <Checkbox type="checkbox" defaultChecked />
                    Exigir autenticação de dois fatores
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <Checkbox type="checkbox" defaultChecked />
                    Logout automático por inatividade
                  </CheckboxLabel>
                </FormSection>

                <FormSection>
                  <SectionTitle>Políticas de Senha</SectionTitle>
                  <FormGroup>
                    <Label>Comprimento mínimo</Label>
                    <Input type="number" defaultValue="8" min="6" max="50" />
                  </FormGroup>
                  <CheckboxLabel>
                    <Checkbox type="checkbox" defaultChecked />
                    Exigir letras maiúsculas
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <Checkbox type="checkbox" defaultChecked />
                    Exigir números
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <Checkbox type="checkbox" defaultChecked />
                    Exigir caracteres especiais
                  </CheckboxLabel>
                </FormSection>

                <Button type="submit" $variant="primary">
                  <Save size={16} />
                  Salvar Configurações
                </Button>
              </Form>
            </ContentBody>
          </>
        );

      case 'backup':
        return (
          <>
            <ContentHeader>
              <ContentTitle>
                <Database size={20} />
                Backup e Restauração
              </ContentTitle>
              <ContentDescription>
                Gerencie backups do sistema e dados
              </ContentDescription>
            </ContentHeader>
            <ContentBody>
              {backupStatus && (
                <Alert $variant={backupStatus === 'success' ? 'success' : 'danger'}>
                  {backupStatus === 'success' ? (
                    <>
                      <CheckCircle size={16} />
                      Operacao concluida com sucesso.
                    </>
                  ) : (
                    <>
                      <AlertCircle size={16} />
                      Falha ao processar backup.
                    </>
                  )}
                </Alert>
              )}
              <FormSection>
                <SectionTitle>Backup Automático</SectionTitle>
                <FormGroup>
                  <Label>Frequência</Label>
                  <Select
                    value={getSetting('backup_frequency', 'daily')}
                    onChange={(e) => updateSystemSetting('backup_frequency', e.target.value)}
                  >
                    <option value="hourly">A cada hora</option>
                    <option value="daily">Diário</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensal</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Horário</Label>
                  <Input
                    type="time"
                    value={getSetting('backup_time', '02:00')}
                    onChange={(e) => updateSystemSetting('backup_time', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Retencao (dias)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={getSetting('backup_retention_days', '30')}
                    onChange={(e) => updateSystemSetting('backup_retention_days', e.target.value)}
                  />
                </FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={getSetting('backup_enabled', 'true') === 'true'}
                    onChange={(e) => updateSystemSetting('backup_enabled', e.target.checked ? 'true' : 'false')}
                  />
                  Backup automático habilitado
                </CheckboxLabel>
                <Button
                  type="button"
                  $variant="primary"
                  onClick={saveSystemSettings}
                  disabled={isSavingSettings}
                  style={{ marginTop: '1rem', width: 'fit-content' }}
                >
                  <Save size={16} />
                  {isSavingSettings ? 'Salvando...' : 'Salvar Configuracoes'}
                </Button>
              </FormSection>

              <FormSection>
                <SectionTitle>Backup Manual</SectionTitle>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <Button $variant="primary" onClick={createBackup} disabled={isCreatingBackup}>
                    <Download size={16} />
                    {isCreatingBackup ? 'Gerando...' : 'Gerar Backup'}
                  </Button>
                  <Button onClick={() => backupInputRef.current?.click()} disabled={isRestoringBackup}>
                    <Upload size={16} />
                    {isRestoringBackup ? 'Restaurando...' : 'Restaurar Backup'}
                  </Button>
                </div>

                <input
                  type="file"
                  accept=".dump,.sql"
                  ref={backupInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!window.confirm('Deseja restaurar este backup? Isso substituira os dados atuais.')) return;
                    const formData = new FormData();
                    formData.append('backup', file);
                    restoreBackup(formData);
                  }}
                />

                <FileUpload onClick={() => backupInputRef.current?.click()}>
                  <Upload size={24} style={{ marginBottom: '0.5rem' }} />
                  <div>Clique para selecionar um arquivo de backup</div>
                  <small style={{ opacity: 0.7 }}>Formatos suportados: .sql, .dump</small>
                </FileUpload>

                <div style={{ marginTop: '1.5rem' }}>
                  <SectionTitle>Backups Disponiveis</SectionTitle>
                  {isLoadingBackup ? (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>Carregando...</div>
                  ) : backupFiles.length === 0 ? (
                    <div style={{ color: '#666' }}>Nenhum backup disponivel.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {backupFiles.map((backup) => (
                        <div key={backup.filename} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{backup.filename}</div>
                            <div style={{ color: '#666', fontSize: '0.85rem' }}>
                              {new Date(backup.last_modified * 1000).toLocaleString('pt-BR')} • {(backup.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <Button
                              type="button"
                              onClick={() => handleDownloadBackup(backup.filename)}
                            >
                              <Download size={16} />
                              Baixar
                            </Button>
                            <Button
                              type="button"
                              $variant="danger"
                              onClick={() => {
                                if (!window.confirm('Deseja restaurar este backup? Isso substituira os dados atuais.')) return;
                                const formData = new FormData();
                                formData.append('filename', backup.filename);
                                restoreBackup(formData);
                              }}
                            >
                              <Upload size={16} />
                              Restaurar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FormSection>
            </ContentBody>
          </>
        );

      default:
        return (
          <ContentBody>
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Seção em desenvolvimento
            </div>
          </ContentBody>
        );
    }
  };

  return (
    <AdminLayout>
      <Container>
        <Header>
          <Title>Configurações do Sistema</Title>
          <Subtitle>Gerencie todas as configurações administrativas do PontoFiel</Subtitle>
        </Header>

        {saveStatus === 'saved' && (
          <Alert $variant="success">
            <CheckCircle size={16} />
            Configurações salvas com sucesso!
          </Alert>
        )}
        {settingsStatus === 'saved' && (
          <Alert $variant="success">
            <CheckCircle size={16} />
            Configurações atualizadas com sucesso!
          </Alert>
        )}
        {settingsStatus === 'error' && (
          <Alert $variant="danger">
            <AlertCircle size={16} />
            Falha ao salvar configuracoes.
          </Alert>
        )}

        <SettingsGrid>
          <Sidebar>
            <SidebarMenu>
              {menuItems.map((item) => (
                <MenuItem
                  key={item.key}
                  $active={activeSection === item.key}
                  onClick={() => setActiveSection(item.key)}
                >
                  {item.icon}
                  {item.label}
                </MenuItem>
              ))}
            </SidebarMenu>
          </Sidebar>

          <Content>
            {renderContent()}
          </Content>
        </SettingsGrid>
      </Container>
    </AdminLayout>
  );
};
